import { partition } from "lodash-es";
import { type FC, type ReactNode, useMemo } from "react";

import { SectionUserItem } from "./SectionUserItem";

import { t } from "i18n/i18n";
import { AccordionPanel } from "mds/components/AccordionPanel";
import { IconText } from "mds/components/IconText";
import { PersonSingleIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  selectActiveClassSessionCategory,
  selectDisabledUserIdsInCurrentSection,
  selectScreensharingClassUserId,
  selectUserIdByClassUserId,
  selectUserIdsInClassSession,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { usePresentation } from "utils/presentation";

interface DisabledUsersCardProps {
  className?: string;
}

export const DisabledUsersCard: FC<DisabledUsersCardProps> = ({ className }) => {
  const userIds = useListSelector(selectDisabledUserIdsInCurrentSection);

  return (
    <SectionUserCard
      className={className}
      headerCount={userIds.length.toString()}
      headerIcon={<PersonSingleIcon size="xs" />}
      headerTitle={t("user_options_menu.suspended_accounts")}
      userIds={userIds}
    />
  );
};

interface SectionUserCardProps {
  userIds: string[];
  className?: string;
  headerTitle: string;
  headerCount: string;
  headerIcon: ReactNode;
  separateUsersInClassSession?: boolean;
  showEmpty?: boolean;
  startOpen?: boolean;
}

export const SectionUserCard: FC<SectionUserCardProps> = ({
  userIds,
  className,
  headerTitle,
  headerCount,
  headerIcon,
  separateUsersInClassSession = false,
  showEmpty = false,
  startOpen = false,
}) => {
  const { presentingUserId } = usePresentation();
  const screensharingClassUserId = useAppSelector(selectScreensharingClassUserId);
  const screensharingUserId = useAppSelector((state) =>
    selectUserIdByClassUserId(state, screensharingClassUserId),
  );
  const classSessionCategory = useAppSelector(selectActiveClassSessionCategory);
  const userIdsInClassSession = useListSelector(selectUserIdsInClassSession);
  const userIdsInClassSessionSet = useMemo(
    () => new Set(userIdsInClassSession),
    [userIdsInClassSession],
  );

  // Order comes from selectSortedUserIdsInCurrentSection; partition preserves it.
  let userIdsInClass: string[];
  let userIdsNotInClass: string[];
  if (classSessionCategory && separateUsersInClassSession) {
    [userIdsInClass, userIdsNotInClass] = partition(userIds, (id) =>
      userIdsInClassSessionSet.has(id),
    );
  } else {
    userIdsInClass = userIds;
    userIdsNotInClass = [];
  }

  if (!showEmpty && userIdsInClass.length === 0 && userIdsNotInClass.length === 0) {
    return null;
  }

  return (
    <AccordionPanel
      childrenWrapperClassName="mt-2.5"
      className={className}
      startOpen={startOpen}
      title={headerTitle}
      titleRightContent={
        <IconText
          className="gap-1 text-sm text-black-tint-20"
          iconEnd={headerIcon}
          text={headerCount}
        />
      }
    >
      {classSessionCategory && separateUsersInClassSession && userIdsInClass.length > 0 && (
        <div className="body-xs ml-1 mt-3 text-black-tint-40">{t("av.present_in_session")}</div>
      )}

      {userIdsInClass.map((userId) => (
        <SectionUserItem
          key={userId}
          presentingUserId={presentingUserId}
          screensharingUserId={screensharingUserId}
          userId={userId}
        />
      ))}

      {classSessionCategory && separateUsersInClassSession && userIdsNotInClass.length > 0 && (
        <div className="body-xs ml-1 mt-3 text-black-tint-40">{t("av.not_present_in_session")}</div>
      )}

      {userIdsNotInClass.map((userId) => (
        <SectionUserItem
          key={userId}
          presentingUserId={presentingUserId}
          screensharingUserId={screensharingUserId}
          userId={userId}
        />
      ))}
    </AccordionPanel>
  );
};
